import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal, alpha } from '@mui/material';

import dayjs from 'dayjs';

import { FilePointerWithMeta, PostgresFilePointer, S3FilePointer } from '@/api/data-contracts';
import placeholderImage from '@/assets/adv_space_placeholder_large.png';
import useUploadedFilesStore from '@/shared/api/uploadedFiles';
import { filesTooLargeMessage } from '@/shared/lib/files';
import { useSnackbar } from '@/shared/lib/hooks/useAppSnackbar';
import { getOrCreateBlobUrl } from '@/shared/lib/hooks/useCachedImage';
import { useFileDropTarget } from '@/shared/lib/hooks/useFileDropTarget';
import { useImagePanZoom } from '@/shared/lib/hooks/useImagePanZoom';
import { ImageResource, usePrefetchResource } from '@/shared/lib/hooks/useResourceImage';
import DeleteConfirmationModal from '@/shared/ui/DeleteConfirmationModal';
import { AttachmentOperationGroup } from '@/shared/ui/ImagesModalVersioning';

// 30MB
import ImagePdfPreview from './ImagePdfPreview';
import ImagePdfSidebar from './ImagePdfSidebar';
import { ModalContainer } from './ImageSlider.styles';

const MAX_FILE_SIZE = 30 * 1024 * 1024; // 30MB

const extractFilePointer = (
  file: PostgresFilePointer | S3FilePointer | FilePointerWithMeta,
): PostgresFilePointer | S3FilePointer => {
  if ('file' in file) {
    return file.file;
  }
  return file;
};

const extractFileMetadata = (
  file: PostgresFilePointer | S3FilePointer | FilePointerWithMeta,
): { uploadedAt?: string; uploadedBy?: Record<string, string> } => {
  if ('file' in file && file.uploadedAt && file.uploadedBy) {
    return {
      uploadedAt: file.uploadedAt,
      uploadedBy: file.uploadedBy,
    };
  }
  return {};
};

interface Props {
  open: boolean;
  onClose: () => void;
  files: (PostgresFilePointer | S3FilePointer | FilePointerWithMeta)[];
  taskId?: string;
  resourceId?: string;
  title?: string;
  buildResource: (documentId: string) => ImageResource;
  onUpload: (files: File[], operationId?: string) => void;
  onDelete: (objectId: string) => void;
  hasCloseButton?: boolean;
  hasDeleteOption?: boolean;
  hasUploadOption?: boolean;
  initialSelectedObjectId?: string;
  operationGroups?: AttachmentOperationGroup[];
  initialExpandedOperationId?: string;
}

const ImagePdfModal: FC<Props> = ({
  open,
  onClose,
  files = [],
  title,
  buildResource,
  onUpload,
  onDelete,
  hasCloseButton = true,
  hasDeleteOption = true,
  hasUploadOption = true,
  initialSelectedObjectId,
  operationGroups,
  initialExpandedOperationId,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const prefetch = usePrefetchResource();
  const [selectedFileId, setSelectedFileId] = useState<number | null>(null);
  const [rotation, setRotation] = useState(0);
  const { zoom, offsetX, offsetY, zoomIn, zoomOut, imageHandlers } =
    useImagePanZoom(selectedFileId);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [fileToDeleteId, setFileToDeleteId] = useState<number | null>(null);
  const didSelectInitialObjectId = useRef(false);
  const [objectUrls, setObjectUrls] = useState<Record<string, string>>({});

  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingUploadOperationIdRef = useRef<string | null>(null);
  const requestedIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      setErrorMessage(null);
    }
  }, [open]);

  useEffect(() => {
    setRotation(0);
  }, [selectedFileId]);

  const staticFiles = useMemo(() => {
    if (!files || !Array.isArray(files)) return [];

    return files.map((f, index) => {
      const fp = extractFilePointer(f);
      const meta = extractFileMetadata(f);
      const numericId = fp.objectId ? parseInt(fp.objectId, 10) : index;
      const safeId = isNaN(numericId) ? index : numericId;

      const uploaderName = meta.uploadedBy?.userName || 'System';
      const uploaderInitial = uploaderName.charAt(0).toUpperCase();
      const uploadedDate = meta.uploadedAt
        ? dayjs(meta.uploadedAt).format('D MMM, YYYY - HH:mm')
        : dayjs().format('D MMM, YYYY - HH:mm');
      const displayDate = meta.uploadedAt
        ? dayjs(meta.uploadedAt).format('D MMM, YYYY')
        : dayjs().format('D MMM, YYYY');

      return {
        id: safeId,
        category: 'General',
        title: fp.name || 'Untitled',
        displayDate,
        realFileName: fp.name || 'unknown',
        fileType: fp.name?.split('.').pop()?.toUpperCase() || 'FILE',
        uploadDate: uploadedDate,
        uploaderName,
        uploaderInitial,
        objectId: fp.objectId,
        thumbnail: placeholderImage,
      };
    });
  }, [files]);

  const allFiles = useMemo(() => {
    return staticFiles.map((file) => ({
      ...file,
      thumbnail:
        file.objectId && objectUrls[file.objectId] ? objectUrls[file.objectId] : file.thumbnail,
    }));
  }, [staticFiles, objectUrls]);

  const fetchImage = useCallback(
    async (objectId: string) => {
      if (requestedIds.current.has(objectId)) return;
      requestedIds.current.add(objectId);

      try {
        const blob = await prefetch(buildResource(objectId));
        if (blob && blob.size > 0) {
          const url = getOrCreateBlobUrl(blob);
          setObjectUrls((prev) => ({ ...prev, [objectId]: url }));
        }
      } catch (error) {
        console.error('Failed to load image', objectId, error);
      }
    },
    [prefetch, buildResource],
  );

  useEffect(() => {
    if (!open) return;
    files.forEach((f) => {
      const fp = extractFilePointer(f);
      if (fp.objectId) fetchImage(fp.objectId);
    });
  }, [open, files, fetchImage]);

  const uploadedFiles = useUploadedFilesStore((s) => s.files);
  const uploadedFileNames = useMemo(() => uploadedFiles.map((f) => f.name), [uploadedFiles]);

  useEffect(() => {
    if (!open) {
      setSelectedFileId(null);
      didSelectInitialObjectId.current = false;
      return;
    }
    if (allFiles.length === 0) return;

    if (initialSelectedObjectId && !didSelectInitialObjectId.current) {
      const match = allFiles.find((f) => f.objectId === initialSelectedObjectId);
      if (match) {
        setSelectedFileId(match.id);
        didSelectInitialObjectId.current = true;
        return;
      }
    }

    if (selectedFileId !== null) return;

    if (uploadedFileNames.length > 0) {
      const lastUploadedName = uploadedFileNames[uploadedFileNames.length - 1];
      const match = allFiles.find((f) => f.realFileName === lastUploadedName);
      if (match) {
        setSelectedFileId(match.id);
        return;
      }
    }

    setSelectedFileId(allFiles[0].id);
  }, [open, allFiles, uploadedFileNames, selectedFileId, initialSelectedObjectId]);

  const selectedFile = useMemo(() => {
    return allFiles.find((f) => f.id === selectedFileId) || allFiles[0] || null;
  }, [allFiles, selectedFileId]);

  const operationSections = useMemo(() => {
    if (!operationGroups?.length) return undefined;
    const byObjectId = new Map(
      allFiles.filter((file) => file.objectId).map((file) => [file.objectId!, file]),
    );
    const claimed = new Set<string>();
    const sections = operationGroups.map((group) => ({
      group,
      files: group.attachmentIds.flatMap((objectId) => {
        claimed.add(objectId);
        const file = byObjectId.get(objectId);
        return file ? [file] : [];
      }),
    }));
    // Unclaimed files land on the LAST section — groups render newest-first, so that is the
    // initial decoration, the same pool home the grouping model uses.
    const leftovers = allFiles.filter((file) => !file.objectId || !claimed.has(file.objectId));
    if (leftovers.length) {
      sections[sections.length - 1].files.push(...leftovers);
    }
    return sections;
  }, [operationGroups, allFiles]);

  const handleUploadClick = () => {
    pendingUploadOperationIdRef.current = null;
    fileInputRef.current?.click();
  };

  const handleUploadToOperation = (uploadOperationId?: string) => {
    pendingUploadOperationIdRef.current = uploadOperationId ?? null;
    fileInputRef.current?.click();
  };

  const uploadPickedFiles = (all: File[]) => {
    if (all.length === 0) return;
    setErrorMessage(null);

    const invalid = all.filter((f) => f.size > MAX_FILE_SIZE);
    const valid = all.filter((f) => f.size <= MAX_FILE_SIZE);

    if (invalid.length) {
      enqueueSnackbar(
        filesTooLargeMessage(
          t,
          invalid.map((f) => f.name),
          MAX_FILE_SIZE,
        ),
        { variant: 'warning' },
      );
    }

    const uploadOperationId = pendingUploadOperationIdRef.current ?? undefined;
    pendingUploadOperationIdRef.current = null;
    if (valid.length) onUpload(valid, uploadOperationId);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;
    const all = Array.from(event.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
    uploadPickedFiles(all);
  };

  const { isDragActive, dropTargetProps } = useFileDropTarget({
    onFiles: (files) => {
      pendingUploadOperationIdRef.current = null;
      uploadPickedFiles(files);
    },
    disabled: !hasUploadOption,
    accept: 'image/*,application/pdf',
    onRejected: () =>
      enqueueSnackbar(t('errors.file_invalid_type', { types: 'image/*, application/pdf' }), {
        variant: 'warning',
      }),
  });

  const handleRequestDelete = (id: number) => {
    setFileToDeleteId(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!fileToDeleteId) return;
    const fileToDelete = allFiles.find((f) => f.id === fileToDeleteId);

    if (fileToDelete && fileToDelete.objectId) {
      onDelete(fileToDelete.objectId);

      if (selectedFileId === fileToDeleteId) {
        const currentIndex = allFiles.findIndex((f) => f.id === fileToDeleteId);
        if (allFiles.length > 1) {
          const nextIndex =
            currentIndex === allFiles.length - 1 ? currentIndex - 1 : currentIndex + 1;
          if (allFiles[nextIndex]) setSelectedFileId(allFiles[nextIndex].id);
        } else {
          setSelectedFileId(null);
        }
      }
    }
    setDeleteModalOpen(false);
    setFileToDeleteId(null);
  };

  const handlePrev = () => {
    if (!selectedFile || allFiles.length === 0) return;
    const currentIndex = allFiles.findIndex((f) => f.id === selectedFile.id);
    const prevIndex = (currentIndex - 1 + allFiles.length) % allFiles.length;
    setSelectedFileId(allFiles[prevIndex].id);
  };

  const handleNext = () => {
    if (!selectedFile || allFiles.length === 0) return;
    const currentIndex = allFiles.findIndex((f) => f.id === selectedFile.id);
    const nextIndex = (currentIndex + 1) % allFiles.length;
    setSelectedFileId(allFiles[nextIndex].id);
  };

  const handleDownload = () => {
    if (selectedFile && selectedFile.objectId && objectUrls[selectedFile.objectId]) {
      const link = document.createElement('a');
      link.href = objectUrls[selectedFile.objectId];
      link.download = selectedFile.realFileName || 'download.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose} data-testid="simulation-modal">
        <ModalContainer
          data-testid="simulation-image-modal"
          {...dropTargetProps}
          sx={
            isDragActive
              ? {
                  backgroundColor: (theme) => alpha(theme.palette.accent.main, 0.1),
                  outline: (theme) => `2px dashed ${theme.palette.accent.main}`,
                  outlineOffset: '-2px',
                }
              : undefined
          }
        >
          <ImagePdfPreview
            data-testid="simulation-modal-preview"
            selectedFile={selectedFile as any}
            rotation={rotation}
            zoom={zoom}
            offsetX={offsetX}
            offsetY={offsetY}
            imageHandlers={imageHandlers}
            hasMultipleFiles={allFiles.length > 1}
            onPrev={handlePrev}
            onNext={handleNext}
            onRotate={() => setRotation((p) => p + 90)}
            onZoomIn={zoomIn}
            onZoomOut={zoomOut}
            onDownload={handleDownload}
            onExpand={() =>
              selectedFile?.objectId && window.open(objectUrls[selectedFile.objectId], '_blank')
            }
            onDelete={() => selectedFile && handleRequestDelete(selectedFile.id)}
            hasDeleteOption={hasDeleteOption}
          />

          <ImagePdfSidebar
            data-testid="simulation-modal-sidebar"
            files={allFiles}
            operationSections={operationSections}
            initialExpandedOperationId={initialExpandedOperationId}
            selectedFileId={selectedFileId}
            errorMessage={errorMessage}
            fileInputRef={fileInputRef}
            title={title}
            onClose={onClose}
            onUploadClick={handleUploadClick}
            onUploadToOperation={handleUploadToOperation}
            onFileChange={handleFileChange}
            onSelectFile={setSelectedFileId}
            onDeleteFile={handleRequestDelete}
            hasCloseButton={hasCloseButton}
            hasDeleteOption={hasDeleteOption}
            hasUploadOption={hasUploadOption}
          />
        </ModalContainer>
      </Modal>

      <DeleteConfirmationModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        data-testid="simulation-delete-confirmation"
      />
    </>
  );
};

export default ImagePdfModal;
